/**
 * What a markdown price step was priced ON (bata-reqs issue 35, owner rulings 2026-10-10;
 * `docs/adr/6024`). The price run records, per step, two bases:
 *
 *   - `elasticity_basis` — `fitted` (the article's own elasticity), `pooled_subcategory` /
 *     `pooled_category` (the pooled elasticity of the article's sub-category or category, used
 *     because its own series was refused or absent; platform ADR 6085, recorded with the pool in
 *     `elasticity_pool_key`), or `fallback` (the market's governed
 *     `markdown.fallback_elasticity`, used because no pool published one either). An accepted
 *     `fitted` coefficient may have been shrunk toward its sub-category or category pool (platform
 *     ADR 6085 amendment, `docs/adr/6026`): it stays `fitted`, and the step records the pool in
 *     `elasticity_shrunk_toward` and the article's own weight in `elasticity_own_weight`;
 *   - `original_price_basis` — `panel_regular_price` (a regular-price week in the store price
 *     panel) or `ticket_price_ex_vat` (the landed Oracle ticket price net of VAT, used because the
 *     panel has no regular-price week).
 *
 * Both are `null` on a step written before the run recorded them: that is "not recorded", never a
 * basis this build infers. A code this build does not know keeps its raw value under an
 * "unrecognised" label and is flagged like a fallback — the platform can add one without a
 * release (G5), and calling it the usual basis would be a guess.
 */

export type BasisKind = 'elasticity' | 'originalPrice';

export interface BasisPresentation {
  readonly kind: BasisKind;
  /** The recorded code, verbatim. */
  readonly code: string;
  readonly labelKey: string;
  readonly helpKey: string;
  /** Not the usual basis: a pool, a fallback, or a code this build cannot name. The list says so. */
  readonly flagged: boolean;
  readonly known: boolean;
  /** The pool a pooled elasticity came from (`<category>` or `<category>/<sub-category>`); else `null`. */
  readonly pool: string | null;
  /** The pool an own elasticity was shrunk toward, with the article's own weight; else `null`. */
  readonly shrinkage: Shrinkage | null;
}

/**
 * Platform ADR 6085 amendment: the article's own coefficient, drawn toward a pool. `toward` is
 * spelled like `elasticity_pool_key`; `ownWeight` is the article's share in (0, 1), the pool
 * carrying the rest — `null` when the run did not record one, never a guessed weight.
 */
export interface Shrinkage {
  readonly toward: string;
  readonly ownWeight: number | null;
}

/** The shrinkage a step recorded; `null` when it names no pool. A weight outside (0, 1] is not shown. */
export function shrinkageOf(bases: StepBases | null | undefined): Shrinkage | null {
  const toward = bases?.elasticity_shrunk_toward;
  if (toward === null || toward === undefined || toward === '') return null;
  const weight = bases?.elasticity_own_weight;
  const ownWeight = typeof weight === 'number' && Number.isFinite(weight) && weight > 0 && weight <= 1 ? weight : null;
  return { toward, ownWeight };
}

interface Known {
  readonly labelKey: string;
  readonly helpKey: string;
  readonly flagged: boolean;
}

const ELASTICITY_BASIS: Readonly<Record<string, Known>> = {
  fitted: { labelKey: 'inseason.priceBasis.elasticity.fitted', helpKey: 'inseason.help.priceBasis.elasticityFitted', flagged: false },
  pooled_subcategory: {
    labelKey: 'inseason.priceBasis.elasticity.pooledSubcategory', helpKey: 'inseason.help.priceBasis.elasticityPooledSubcategory', flagged: true,
  },
  pooled_category: {
    labelKey: 'inseason.priceBasis.elasticity.pooledCategory', helpKey: 'inseason.help.priceBasis.elasticityPooledCategory', flagged: true,
  },
  fallback: { labelKey: 'inseason.priceBasis.elasticity.fallback', helpKey: 'inseason.help.priceBasis.elasticityFallback', flagged: true },
};

const ORIGINAL_PRICE_BASIS: Readonly<Record<string, Known>> = {
  panel_regular_price: {
    labelKey: 'inseason.priceBasis.originalPrice.panelRegularPrice', helpKey: 'inseason.help.priceBasis.originalPricePanel', flagged: false,
  },
  ticket_price_ex_vat: {
    labelKey: 'inseason.priceBasis.originalPrice.ticketPriceExVat', helpKey: 'inseason.help.priceBasis.originalPriceTicket', flagged: true,
  },
};

export const UNRECOGNISED_BASIS_KEY = 'inseason.priceBasis.unrecognised';
export const UNRECOGNISED_BASIS_HELP_KEY = 'inseason.help.priceBasis.unrecognised';

function present(
  kind: BasisKind, table: Readonly<Record<string, Known>>, code: string | null | undefined, pool: string | null = null,
  shrinkage: Shrinkage | null = null,
): BasisPresentation | null {
  if (code === null || code === undefined || code === '') return null;
  const known = table[code];
  return known === undefined
    ? { kind, code, labelKey: UNRECOGNISED_BASIS_KEY, helpKey: UNRECOGNISED_BASIS_HELP_KEY, flagged: true, known: false, pool, shrinkage }
    : { kind, code, ...known, known: true, pool, shrinkage };
}

/**
 * The elasticity a step was priced on; `null` when the run did not record it. `pool` is the
 * recorded `elasticity_pool_key`, kept only when it is a non-empty string; `shrinkage` is what
 * `shrinkageOf` read. Shrinkage never flags a basis: it is the usual case for an own elasticity.
 */
export function elasticityBasis(
  code: string | null | undefined, pool?: string | null, shrinkage: Shrinkage | null = null,
): BasisPresentation | null {
  return present('elasticity', ELASTICITY_BASIS, code, pool === undefined || pool === null || pool === '' ? null : pool, shrinkage);
}

/** Where a step's original price came from; `null` when the run did not record it. */
export function originalPriceBasis(code: string | null | undefined): BasisPresentation | null {
  return present('originalPrice', ORIGINAL_PRICE_BASIS, code);
}

/** The two bases a step or a posed problem may carry. */
export interface StepBases {
  readonly elasticity_basis?: string | null;
  readonly original_price_basis?: string | null;
  /** Platform ADR 6085: the pool of a pooled elasticity basis. */
  readonly elasticity_pool_key?: string | null;
  /** Platform ADR 6085 amendment: the pool an own elasticity was shrunk toward. */
  readonly elasticity_shrunk_toward?: string | null;
  /** Platform ADR 6085 amendment: the article's own weight in that shrinkage, in (0, 1). */
  readonly elasticity_own_weight?: number | null;
}

/** The bases of one step that are not the usual one, elasticity first — what a list must say. */
export function flaggedBases(step: StepBases): readonly BasisPresentation[] {
  return [elasticityBasis(step.elasticity_basis, step.elasticity_pool_key), originalPriceBasis(step.original_price_basis)]
    .filter((basis): basis is BasisPresentation => basis !== null && basis.flagged);
}
